import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { Tables } from '../lib/types';

interface NewTravelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (travelData: any) => void;
  initialData?: Tables<'travel_packages'>;
}

const NewTravelModal: React.FC<NewTravelModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [travelData, setTravelData] = useState({
    title: '',
    description: '',
    location: '',
    startDate: '',
    endDate: '',
    participantsRange: '',
    price: '',
    imageUrl: ''
  });

  useEffect(() => {
    if (initialData) {
      setTravelData({
        title: initialData.title,
        description: initialData.description,
        location: initialData.location,
        startDate: initialData.start_date,
        endDate: initialData.end_date,
        participantsRange: initialData.participants_range,
        price: initialData.price.toString(),
        imageUrl: initialData.image_url
      });
    } else {
      setTravelData({
        title: '',
        description: '',
        location: '',
        startDate: '',
        endDate: '',
        participantsRange: '',
        price: '',
        imageUrl: ''
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(travelData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Modifica Viaggio' : 'Nuovo Viaggio'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Titolo</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={travelData.title}
              onChange={(e) => setTravelData({ ...travelData, title: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Descrizione</label>
            <textarea
              required
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={travelData.description}
              onChange={(e) => setTravelData({ ...travelData, description: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Location</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={travelData.location}
              onChange={(e) => setTravelData({ ...travelData, location: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Data Inizio</label>
              <input
                type="date"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={travelData.startDate}
                onChange={(e) => setTravelData({ ...travelData, startDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Data Fine</label>
              <input
                type="date"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={travelData.endDate}
                onChange={(e) => setTravelData({ ...travelData, endDate: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Range Partecipanti</label>
            <input
              type="text"
              required
              placeholder="es: 15-20"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={travelData.participantsRange}
              onChange={(e) => setTravelData({ ...travelData, participantsRange: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Prezzo (€)</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={travelData.price}
              onChange={(e) => setTravelData({ ...travelData, price: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">URL Immagine</label>
            <input
              type="url"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
              value={travelData.imageUrl}
              onChange={(e) => setTravelData({ ...travelData, imageUrl: e.target.value })}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
            >
              {initialData ? 'Salva Modifiche' : 'Crea Viaggio'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTravelModal;